import { useRef, useCallback, useEffect, useState } from "react";
import {
  Button,
  Box,
  Grid,
  AppBar,
  Tabs,
  Tab,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from 'react-router-dom';

import CreatePolicyForm from "./CreatePolicyForm";
import PolicyDriver from "../PolicyDetails/PolicyDriver";
import PolicyVehicle from "../PolicyDetails/PolicyVehicle";
import PremiumDetails from "../PolicyDetails/PremiumDetails";
import AddNotesTable from "../../notes/AddNotesTable";
import TaskComponent from "../PolicyDetails/Task";
import Attachments from "../../attachments/Attachments";
import CustomerDetail from "./CustomerDetail";
import PolicyDetail from "./PolicyDetail";
import CustomerFileExplorer from "../../attachments/CustomerFilesSection";

const CreatePolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [resetSignal, setResetSignal] = useState(0);
  const [value, setValue] = useState(0);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [driverflag, setDriverFlag] = useState(false);
  const [policyId, setPolicyId] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const [premiumId, setPremiumId] = useState(null);
  const [getNewPolicyNumber, setGetNewPolicyNumber] = useState(null);
  const [clientCustomerId, setClientCustomerId] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [forceResetSignal, setForceResetSignal] = useState(0);
  const flag = true;

  // Track which accordions are expanded
  const [expandedPanels, setExpandedPanels] = useState({
    details: true,
    customer: false,
    policy: false,
    drivers: false,
    vehicle: false,
    coverage: false,
  });

  // Optional local cache of initial data (Customer/Policy are useful here)
  const [prefetch, setPrefetch] = useState({
    customer: null,
    policy: null,
    drivers: null,
    vehicles: null,
    premiums: null,
  });

  const hydratedOnce = useRef(false);

useEffect(() => {
  const fromMenu = location.state?.fromMenu;
  const askToReset = location.state?.askToReset;
  if (!fromMenu && !askToReset) return;

  // ensure the form is mounted
  setStep(1);
  setExpandedPanels(p => ({ ...p, details: true }));

  // If we’re on step 2 (or from menu), force a hard reset (no confirm)
  if (step === 2 || fromMenu) {
    setForceResetSignal(s => s + 1);
  } else {
    setResetSignal(s => s + 1);
  }

  // clear nav state
  navigate(location.pathname, { replace: true, state: {} });
}, [location.state?.fromMenu, location.state?.askToReset]); 

  // Prefetch everything ONCE using the ids returned by the form
  const hydrateAll = useCallback(    
    async (ids) => {
      if (!ids?.policyId || !ids?.clientCustomerId) return;
      if (hydratedOnce.current) return;
      hydratedOnce.current = true;

    const [customerRes, policyRes, driversRes, vehiclesRes, premiumsRes] =
      await Promise.allSettled();
      
      const val = (r) => (r.status === "fulfilled" ? r.value : null);

      const customer = val(customerRes);
      const policy = val(policyRes);

      setPrefetch({
      customer: val(customerRes),
      policy: val(policyRes),
      drivers: val(driversRes),
      vehicles: val(vehiclesRes),
      premiums: val(premiumsRes),
      });
    },
    []
  );

// put this once (top of component is fine)
const wantsReset =
  Boolean(location.state?.askToReset) || Boolean(location.state?.fromMenu);

useEffect(() => {
  if (!wantsReset) return;

  // ensure the form is visible
  setStep(1);
  setExpandedPanels(p => (p.details ? p : { ...p, details: true }));

  // choose which signal to bump (do not bump both)
  if (location.state?.fromMenu || step === 2) {
    setForceResetSignal(s => s + 1);
  } else {
    setResetSignal(s => s + 1);
  }

  // clear the triggering state so it cannot fire again
  navigate(location.pathname, { replace: true, state: {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [wantsReset]); // <-- single dependency


  const handleFormSaved = useCallback(
    (ids) => {
      // ids = { policyId, clientCustomerId, policyNumber, ... }
      setPolicyId(ids.policyId);
      setClientCustomerId(ids.clientCustomerId);
      setGetNewPolicyNumber(ids.policyNumber);
      setStep(2);
      hydrateAll(ids); // fetch everything ONCE here
    },
    [hydrateAll]
  );

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  const handleBack = () => setStep(1);

  const handleSubmit = () => {
    setIsSubmitClicked(true);
    setValue(1);
  };

  const handleChange = (_event, newValue) => {
    if (isSubmitClicked || newValue === 0) setValue(newValue);
  };

  return (
    <div style={{ padding: "0 40px", marginTop: "50px" }}>
      <Grid item xs={12}>
        <Box className="policy-detail-main-container">
          <AppBar position="static" id="policydetailsappbar">
            <Tabs value={value} onChange={handleChange} aria-label="simple tabs example">
              <Tab label="Policy Details" id="tabpolicydetails" />
              <Tab label="Notes" id="tabnotes" disabled={!isSubmitClicked} />
              <Tab label="Task" id="tabtask" disabled={!isSubmitClicked} />
              <Tab label="Policy Documents" id="tabpolicydocuments" disabled={!isSubmitClicked} />
            </Tabs>
          </AppBar> 

          <TabPanel value={value} index={0} id="policyinfotabpanel">
            {step === 1 && (
                <Accordion
                  expanded={expandedPanels.details}
                  onChange={handleAccordionChange("details")}
                  TransitionProps={{ unmountOnExit: false }}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1header">
                    <Typography sx={{ fontWeight: "500", fontSize: "19px" }}>Details</Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <CreatePolicyForm
                      resetSignal={resetSignal}
                      forceResetSignal={forceResetSignal}
                      setStep={setStep}
                      setDriverFlag={setDriverFlag}
                      setPolicyId={setPolicyId}
                      setGetNewPolicyNumber={setGetNewPolicyNumber}
                      setClientCustomerId={setClientCustomerId}
                      clientCustomerId={clientCustomerId}
                      setSavePolicyDetails={() => {}}
                      onSaved={handleFormSaved}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

            {step === 2 && (
              <>
                <Accordion
                  expanded={expandedPanels.customer}
                  onChange={handleAccordionChange("customer")}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2header">
                    <Typography sx={{ fontWeight: "500", fontSize: "18px" }}>Customer Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CustomerDetail 
                        clientCustomerId={clientCustomerId} 
                        data={prefetch.customer}
                      />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedPanels.policy}
                  onChange={handleAccordionChange("policy")}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2header">
                    <Typography sx={{ fontWeight: "500", fontSize: "18px" }}>Policy Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PolicyDetail  
                        policyId={policyId}
                        data={prefetch.policy}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedPanels.drivers}
                  onChange={handleAccordionChange("drivers")}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2header">
                    <Typography sx={{ fontWeight: "500", fontSize: "18px" }}>Drivers</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PolicyDriver
                        setDriverId={setDriverId}
                        policyId={policyId}
                        data={prefetch.drivers}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedPanels.vehicle}
                  onChange={handleAccordionChange("vehicle")}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3-content" id="panel3header">
                    <Typography sx={{ fontWeight: "500", fontSize: "18px" }}>Vehicle</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PolicyVehicle
                        policyId={policyId}
                        driverId={driverId}
                        setPremiumId={setPremiumId}
                        setVehicleId={setVehicleId}
                        data={prefetch.vehicles}
                      />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedPanels.coverage}
                  onChange={handleAccordionChange("coverage")}
                  sx={{
                    margin: "0 0 10px 0",
                    width: "calc(100% + 2px)",
                    background: "#F4F5F9",
                    boxShadow: "none",
                    border: "#D9D9D9 solid 1px",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel4-content" id="panel4header">
                    <Typography sx={{ fontWeight: "500", fontSize: "18px" }}>Coverage</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                   <PremiumDetails
                        policyId={premiumId}
                        vehicleId={vehicleId}
                        data={prefetch.premiums}
                    />
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            {/* Step Navigation Buttons */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
              {step === 2 && (
                <>
                  <Button onClick={handleBack}
                  variant="outlined"
                  size="large"
                  >
                    Back
                  </Button>
                  <Button onClick={handleSubmit}
                  variant="outlined"
                  size="large"
                  >
                    Submit
                  </Button>
                </>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={value} index={1} id="notestabpanel">
            {value === 1 && (
              <AddNotesTable 
              notesPolicyId={policyId} 
              getNewPolicyNumber={getNewPolicyNumber} />
            )}
          </TabPanel>

          <TabPanel value={value} index={2} id="tasktabpanel">
            {value === 2 && (
              <TaskComponent
                policyId={policyId}
                getPolicyNumber={getNewPolicyNumber}
                showOnlyAddTask={flag}
                clientCustomerIdTask={clientCustomerId}
              />
            )}
          </TabPanel>

          <TabPanel value={value} index={3} id="attachmentstabpanel">
            {value === 3 && <CustomerFileExplorer policynum={getNewPolicyNumber} policyId={policyId} />}
          </TabPanel>

          <TabPanel value={value} index={4} id="activitytabpanel">
            {value === 4 && "Activity content here..."}
          </TabPanel>
        </Box>
      </Grid>
    </div>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`simpletabpanel${index}`} aria-labelledby={`simpletab${index}`} {...other}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default CreatePolicy;
